import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import './index.css';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import MainWrapper from './pages/MainWrapper';
import UserManagement from './pages/UserManagement';
import ClientManagement from './pages/ClientManagement';
import ProductManagement from './pages/ProductManagement';
import DueDateManagement from './pages/DueDateManagement';
import OrderManagement from './pages/OrderManagement';
import NuevoPedido from './pages/NuevoPedido';
import OrderSuccess from './pages/OrderSuccess';
import PriceManagement from './pages/PriceManagement';
import InvoiceUpload from './pages/InvoiceUpload';
import FacturasListado from './pages/FacturasListado';
import AlbaranUpload from './pages/AlbaranUpload';
import CommercialStats from './pages/CommercialStats';
import Comisiones from './pages/Comisiones';
import VencimientoManagement from './pages/VencimientoManagement';
import RemesasVencimientos from './pages/RemesasVencimientos';
import RemesaGenerator from './pages/RemesaGenerator';
import EstadoRemesas from './pages/EstadoRemesas';
import AgenciasTransporte from './pages/AgenciasTransporte';
import CatalogoProductos from './pages/CatalogoProductos';
import ComercialHome from './pages/ComercialHome';
import ComercialClientes from './pages/ComercialClientes';
import ComercialOfertas from './pages/ComercialOfertas';
import AdminOfertas from './pages/AdminOfertas';
import ComercialPedidosCliente from './pages/ComercialPedidosCliente';
import ComercialPreciosCliente from './pages/ComercialPreciosCliente';
import ClienteHome from './pages/ClienteHome';
import ClientePedidos from './pages/ClientePedidos';
import ClienteFacturacion from './pages/ClienteFacturacion';
import ClienteAnalisis from './pages/ClienteAnalisis';
import ClientesAsociados from './pages/ClientesAsociados';
import RecoverPassword from './pages/RecoverPassword';

// Role-based redirect after login
const RoleRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;

  switch (user.permisos) {
    case 1: case 2: case 6: return <Navigate to="/gestion" />;
    case 3: return <Navigate to="/comercial" />;
    case 5: return <Navigate to="/cliente" />;
    default: return <Navigate to="/login" />;
  }
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<HomePage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/recuperar-password" element={<RecoverPassword />} />

      {/* Post-login redirect */}
      <Route path="/redirect" element={<ProtectedRoute><RoleRedirect /></ProtectedRoute>} />

      {/* Admin / Operador / SuperAdmin */}
      <Route path="/gestion" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><MainWrapper /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/usuarios" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><UserManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/clientes" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 6]}>
          <Layout><ClientManagement /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/productos" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><ProductManagement /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/comercial/facturas" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><InvoiceUpload /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/albaranes" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><AlbaranUpload /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/comisiones" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><Comisiones /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/clientes-asociados/:userId" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><ClientesAsociados /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/estadisticas" element={
        <ProtectedRoute allowedRoles={[1, 3, 6]}>
          <Layout><CommercialStats /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/pedidos" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><OrderManagement defaultFilter="confirmados" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pedidos/abiertos" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><OrderManagement defaultFilter="abiertos" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pedidos/confirmados" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><OrderManagement defaultFilter="confirmados" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pedidos/buscar" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 6]}>
          <Layout><OrderManagement defaultFilter="todos" /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pedidos/nuevo" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 5, 6]}>
          <Layout><NuevoPedido /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pedidos/confirmado" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 5, 6]}>
          <Layout><OrderSuccess /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/precios" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 6]}>
          <Layout><PriceManagement /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/contabilidad/facturas" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><FacturasListado /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contabilidad/vencimientos" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><VencimientoManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contabilidad/vencimientos-crud" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><DueDateManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contabilidad/remesas-vencimientos" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><RemesasVencimientos /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contabilidad/remesas" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><RemesaGenerator /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contabilidad/estado-remesas" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><EstadoRemesas /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/transportes/agencias" element={
        <ProtectedRoute allowedRoles={[1, 2, 6]}>
          <Layout><AgenciasTransporte /></Layout>
        </ProtectedRoute>
      } />
      {/* Ofertas (admin) */}
      <Route path="/ofertas" element={
        <ProtectedRoute allowedRoles={[1, 6]}>
          <Layout><AdminOfertas /></Layout>
        </ProtectedRoute>
      } />

      {/* Commercial portal */}
      <Route path="/comercial" element={
        <ProtectedRoute allowedRoles={[3]}>
          <Layout><ComercialHome /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/clientes" element={
        <ProtectedRoute allowedRoles={[3]}>
          <Layout><ComercialClientes /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/ofertas" element={
        <ProtectedRoute allowedRoles={[3]}>
          <Layout><ComercialOfertas /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/pedidos-cliente/:clientId" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 6]}>
          <Layout><ComercialPedidosCliente /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/comercial/precios-cliente/:clientId" element={
        <ProtectedRoute allowedRoles={[1, 2, 3, 6]}>
          <Layout><ComercialPreciosCliente /></Layout>
        </ProtectedRoute>
      } />

      {/* Client portal */}
      <Route path="/cliente" element={
        <ProtectedRoute allowedRoles={[5]}>
          <Layout><ClienteHome /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/cliente/pedidos" element={
        <ProtectedRoute allowedRoles={[5]}>
          <Layout><ClientePedidos /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/cliente/facturacion" element={
        <ProtectedRoute allowedRoles={[5]}>
          <Layout><ClienteFacturacion /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/cliente/nuevo-pedido" element={
        <ProtectedRoute allowedRoles={[5]}>
          <Layout><NuevoPedido /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/cliente/analisis" element={
        <ProtectedRoute allowedRoles={[5]}>
          <Layout><ClienteAnalisis /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/catalogo" element={
        <ProtectedRoute allowedRoles={[1, 3, 5, 6]}>
          <Layout><CatalogoProductos /></Layout>
        </ProtectedRoute>
      } />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
